import { useEffect, useId, useRef, useState } from "react";
import { api, ApiError } from "./lib/api";
import { formatDateTime, formatMoney } from "./lib/format";
import { getPublicEnv } from "./lib/publicEnv";
import type {
  AdminEvent,
  AdminMe,
  AdminPromoCode,
  OrderDetail,
  OrderSummary,
  PublicEvent,
  SessionStatus,
  TicketType
} from "./lib/types";

type Route =
  | { name: "home" }
  | { name: "events" }
  | { name: "event"; slug: string }
  | { name: "checkout-success" }
  | { name: "checkout-cancel" }
  | { name: "static"; page: StaticPageName }
  | { name: "admin-home" }
  | { name: "admin-events" }
  | { name: "admin-event-new" }
  | { name: "admin-event"; id: string }
  | { name: "admin-orders" }
  | { name: "admin-order"; id: string }
  | { name: "admin-checkin"; eventId: string }
  | { name: "admin-settings" }
  | { name: "not-found" };

type StaticPageName = "terms" | "privacy" | "refund-policy" | "contact";

type AsyncState<T> =
  | { status: "idle" | "loading" }
  | { status: "success"; data: T }
  | { status: "error"; message: string };

type FieldOption = {
  value: string;
  label: string;
};

type SiteSettingsFormState = {
  siteName: string;
  supportEmail: string;
  heroNotice: string;
  emailEnabled: string;
  fromEmail: string;
};

type PromoCodeFormState = {
  id?: string;
  code: string;
  discountType: "percent" | "amount";
  discountValue: string;
  currency: string;
  maxRedemptions: string;
  expiresAt: string;
  isActive: boolean;
  isArchived?: boolean;
};

type PublicSiteSettings = {
  siteName: string;
  supportEmail: string;
  heroNotice: string;
};

const publicEnv = getPublicEnv();
const eventStatusOptions: FieldOption[] = [
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
  { value: "closed", label: "Closed" },
  { value: "archived", label: "Archived" }
];
const currencyOptions: FieldOption[] = [
  { value: "GBP", label: "GBP (£)" },
  { value: "EUR", label: "EUR (€)" },
  { value: "USD", label: "USD ($)" }
];
const yesNoOptions: FieldOption[] = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" }
];
const promoDiscountTypeOptions: FieldOption[] = [
  { value: "percent", label: "Percent off" },
  { value: "amount", label: "Fixed amount off" }
];
const emailEnabledOptions: FieldOption[] = [
  { value: "false", label: "Keep email off" },
  { value: "true", label: "Enable email sending" }
];
const orderStatusOptions: FieldOption[] = [
  { value: "", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "paid", label: "Paid" },
  { value: "expired", label: "Expired" },
  { value: "cancelled", label: "Cancelled" },
  { value: "refunded", label: "Refunded" }
];
const maxHeroImageBytes = 1_500_000;
const defaultSettingsForm: SiteSettingsFormState = {
  siteName: publicEnv.VITE_APP_NAME,
  supportEmail: "tickets@example.com",
  heroNotice: "Limited release tickets for intimate late-night events.",
  emailEnabled: "false",
  fromEmail: ""
};
const defaultPublicSiteSettings: PublicSiteSettings = {
  siteName: defaultSettingsForm.siteName,
  supportEmail: defaultSettingsForm.supportEmail,
  heroNotice: defaultSettingsForm.heroNotice
};

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Image upload failed while reading the selected file."));
    reader.onload = () => {
      if (typeof reader.result !== "string") {
        reject(new Error("Image upload failed because the selected file could not be encoded."));
        return;
      }
      resolve(reader.result);
    };
    reader.readAsDataURL(file);
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function toDateTimeLocalValue(value: string): string {
  if (!value.trim()) {
    return "";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  const local = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function fromDateTimeLocalValue(value: string): string {
  if (!value.trim()) {
    return "";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toISOString();
}

function toTicketTypeFormState(ticketType: TicketType) {
  return {
    eventId: ticketType.eventId,
    name: ticketType.name,
    description: ticketType.description,
    priceMinor: ticketType.priceMinor,
    currency: ticketType.currency,
    inventoryTotal: ticketType.inventoryTotal,
    perOrderLimit: ticketType.perOrderLimit,
    saleStartAt: toDateTimeLocalValue(ticketType.saleStartAt),
    saleEndAt: toDateTimeLocalValue(ticketType.saleEndAt),
    isVisible: ticketType.isVisible,
    displayOrder: ticketType.displayOrder
  };
}

function parseRoute(pathname: string): Route {
  const clean = pathname.replace(/\/+$/, "") || "/";
  if (clean === "/") {
    return { name: "home" };
  }
  if (clean === "/events") {
    return { name: "events" };
  }
  if (clean.startsWith("/events/")) {
    return { name: "event", slug: decodeURIComponent(clean.slice("/events/".length)) };
  }
  if (clean === "/checkout/success") {
    return { name: "checkout-success" };
  }
  if (clean === "/checkout/cancel") {
    return { name: "checkout-cancel" };
  }
  if (clean === "/terms" || clean === "/privacy" || clean === "/refund-policy" || clean === "/contact") {
    return { name: "static", page: clean.slice(1) as StaticPageName };
  }
  if (clean === "/admin") {
    return { name: "admin-home" };
  }
  if (clean === "/admin/events") {
    return { name: "admin-events" };
  }
  if (clean === "/admin/events/new") {
    return { name: "admin-event-new" };
  }
  if (clean.startsWith("/admin/events/")) {
    return { name: "admin-event", id: decodeURIComponent(clean.slice("/admin/events/".length)) };
  }
  if (clean === "/admin/orders") {
    return { name: "admin-orders" };
  }
  if (clean.startsWith("/admin/orders/")) {
    return { name: "admin-order", id: decodeURIComponent(clean.slice("/admin/orders/".length)) };
  }
  if (clean.startsWith("/admin/checkin/")) {
    return { name: "admin-checkin", eventId: decodeURIComponent(clean.slice("/admin/checkin/".length)) };
  }
  if (clean === "/admin/settings") {
    return { name: "admin-settings" };
  }
  return { name: "not-found" };
}

function useRoute() {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname));

  useEffect(() => {
    const onPopState = () => setRoute(parseRoute(window.location.pathname));
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  return route;
}

function useLoad<T>(loader: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [state, setState] = useState<AsyncState<T>>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    loader()
      .then((data) => {
        if (!cancelled) {
          setState({ status: "success", data });
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({
            status: "error",
            message: error instanceof Error ? error.message : "Request failed"
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, deps);

  return state;
}

function usePublicSiteSettings() {
  return useLoad(() => api<{ settings: PublicSiteSettings }>("/api/settings"), []);
}

function statusClass(status: string): string {
  if (status === "pending") {
    return "status-pill pending";
  }
  if (status === "expired" || status === "cancelled" || status === "refunded") {
    return `status-pill ${status}`;
  }
  if (status === "processing") {
    return "status-pill processing";
  }
  return `status-pill ${status}`;
}

function PublicShell({ children }: { children: React.ReactNode }) {
  const settingsState = usePublicSiteSettings();
  const settings = settingsState.status === "success" ? settingsState.data.settings : defaultPublicSiteSettings;

  return (
    <div className="page public-page">
      <div className="shell">
        <header className="masthead">
          <div className="brand">
            <strong>{settings.siteName}</strong>
            <span>Evening programme and ticket reservations</span>
          </div>
          <nav className="nav">
            <a href="/">Foyer</a>
            <a href="/events">Programme</a>
            <a href="/contact">Contact</a>
          </nav>
        </header>
        {children}
      </div>
    </div>
  );
}

function AdminShell({
  title,
  me,
  children
}: {
  title: string;
  me: AdminMe;
  children: React.ReactNode;
}) {
  return (
    <div className="page admin-page">
      <div className="shell">
        <header className="masthead">
          <div className="brand">
            <strong>Admin Console</strong>
            <span>
              {me.email} · {me.role}
            </span>
          </div>
          <nav className="nav">
            <a href="/admin">Overview</a>
            <a href="/admin/events">Events</a>
            <a href="/admin/orders">Orders</a>
            <a href="/admin/settings">Settings</a>
          </nav>
        </header>
        <section className="section">
          <div className="section-head">
            <div>
              <div className="eyebrow">Admin</div>
              <h1 className="admin-title">{title}</h1>
            </div>
          </div>
          {children}
        </section>
      </div>
    </div>
  );
}

function LoadGate<T>({
  state,
  children
}: {
  state: AsyncState<T>;
  children: (data: T) => React.ReactNode;
}) {
  if (state.status === "loading" || state.status === "idle") {
    return (
      <div className="panel">
        <p className="muted">Loading…</p>
      </div>
    );
  }
  if (state.status === "error") {
    return <div className="error-banner">{state.message}</div>;
  }
  if (state.status === "success") {
    return <>{children(state.data)}</>;
  }
  return null;
}

function EventCard({ event }: { event: PublicEvent }) {
  return (
    <article className="card">
      <div className="card-image" style={{ backgroundImage: `url(${event.heroImageUrl})` }} />
      <div className="card-body stack">
        <div className="eyebrow">{formatDateTime(event.startAt)}</div>
        <h3>{event.title}</h3>
        <p>{event.shortDescription}</p>
        <div className="actions">
          <a className="button" href={`/events/${event.slug}`}>
            View tickets
          </a>
          <span className="muted">{event.venueName}</span>
        </div>
      </div>
    </article>
  );
}

function HomePage() {
  const settingsState = usePublicSiteSettings();
  const settings = settingsState.status === "success" ? settingsState.data.settings : defaultPublicSiteSettings;
  const eventsState = useLoad(() => api<{ events: PublicEvent[] }>("/api/events"), []);

  return (
    <PublicShell>
      <LoadGate state={eventsState}>
        {({ events }) => {
          const featured = events[0];
          return (
            <>
              <section className="hero">
                <div className="stack">
                  <div className="eyebrow">Noir Nights ticket office</div>
                  <h1>Curated nights, limited capacity, and a faster way to secure your place.</h1>
                  <p>
                    Explore the current programme, choose your date, and reserve tickets in a few straightforward steps.
                  </p>
                  <div className="notice">{settings.heroNotice}</div>
                  <div className="actions">
                    <a className="button" href="/events">
                      Browse the programme
                    </a>
                  </div>
                </div>
                {featured ? (
                  <div className="hero-image" style={{ backgroundImage: `url(${featured.heroImageUrl})` }}>
                    <div className="hero-meta">
                      <strong>{featured.title}</strong>
                      <div>{formatDateTime(featured.startAt)}</div>
                    </div>
                  </div>
                ) : (
                  <div className="panel">
                    <p>Fresh dates are being prepared. Please check back soon.</p>
                  </div>
                )}
              </section>
              <section className="section">
                <div className="section-head">
                  <div>
                    <div className="eyebrow">Events</div>
                    <h2>Now booking</h2>
                  </div>
                </div>
                <div className="cards">{events.map((event) => <EventCard key={event.id} event={event} />)}</div>
              </section>
            </>
          );
        }}
      </LoadGate>
    </PublicShell>
  );
}

function EventsPage() {
  const eventsState = useLoad(() => api<{ events: PublicEvent[] }>("/api/events"), []);
  return (
    <PublicShell>
      <section className="section">
        <div className="section-head">
          <div>
            <div className="eyebrow">Events</div>
            <h2>Choose your night</h2>
          </div>
        </div>
        <LoadGate state={eventsState}>
          {({ events }) => <div className="cards">{events.map((event) => <EventCard key={event.id} event={event} />)}</div>}
        </LoadGate>
      </section>
    </PublicShell>
  );
}

function EventDetailPage({ slug }: { slug: string }) {
  const eventState = useLoad(() => api<{ event: PublicEvent }>(`/api/events/${slug}`), [slug]);
  const [buyerName, setBuyerName] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [note, setNote] = useState("");
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string>("");

  return (
    <PublicShell>
      <LoadGate state={eventState}>
        {({ event }) => {
          const selectedTickets = event.ticketTypes.filter((ticketType) => (quantities[ticketType.id] ?? 0) > 0);
          const total = selectedTickets.reduce(
            (sum, ticketType) => sum + ticketType.priceMinor * (quantities[ticketType.id] ?? 0),
            0
          );

          async function handleCheckout(eventSubmit: React.FormEvent<HTMLFormElement>) {
            eventSubmit.preventDefault();
            setSubmitting(true);
            setMessage("");
            try {
              const response = await api<{ checkout_url: string; public_order_no: string }>("/api/checkout/session", {
                method: "POST",
                body: JSON.stringify({
                  event_slug: event.slug,
                  buyer_name: buyerName,
                  buyer_email: buyerEmail,
                  note,
                  items: selectedTickets.map((ticketType) => ({
                    ticket_type_id: ticketType.id,
                    quantity: quantities[ticketType.id],
                    attendees: Array.from({ length: quantities[ticketType.id] ?? 0 }, () => ({
                      name: buyerName,
                      email: buyerEmail
                    }))
                  }))
                })
              });
              window.location.assign(response.checkout_url);
            } catch (error) {
              setMessage(error instanceof Error ? error.message : "We could not start checkout just now.");
            } finally {
              setSubmitting(false);
            }
          }

          return (
            <>
              <section className="hero">
                <div className="stack">
                  <div className="eyebrow">{formatDateTime(event.startAt)}</div>
                  <h1>{event.title}</h1>
                  <p>{event.longDescription}</p>
                  <div className="meta-list">
                    <div>
                      <strong>Venue</strong>
                      <span>{event.venueName}</span>
                    </div>
                    <div>
                      <strong>Address</strong>
                      <span>{event.venueAddress}</span>
                    </div>
                    <div>
                      <strong>Refund policy</strong>
                      <span>{event.refundPolicy}</span>
                    </div>
                  </div>
                </div>
                <div className="hero-image" style={{ backgroundImage: `url(${event.heroImageUrl})` }}>
                  <div className="hero-meta">
                    <strong>{event.venueName}</strong>
                    <div>{formatDateTime(event.startAt)}</div>
                  </div>
                </div>
              </section>

              <section className="section split">
                <div className="panel stack">
                  <div className="section-head">
                    <div>
                      <div className="eyebrow">Tickets</div>
                      <h2>Choose your ticket</h2>
                    </div>
                  </div>
                  {event.ticketTypes.length === 0 ? (
                    <div className="notice">Tickets for this event are not on sale yet. Please check back soon.</div>
                  ) : (
                    <div className="ticket-grid">
                      {event.ticketTypes.map((ticketType) => (
                        <div className="card" key={ticketType.id}>
                          <div className="card-body stack">
                            <h3>{ticketType.name}</h3>
                            <p>{ticketType.description}</p>
                            <div className="meta-list">
                              <div>
                                <strong>Price</strong>
                                <span>{formatMoney(ticketType.priceMinor, ticketType.currency)}</span>
                              </div>
                              <div>
                                <strong>Available</strong>
                                <span>{ticketType.availableInventory}</span>
                              </div>
                              <div className="field">
                                <label htmlFor={`qty-${ticketType.id}`}>Quantity</label>
                                <input
                                  id={`qty-${ticketType.id}`}
                                  type="number"
                                  min={0}
                                  max={Math.min(ticketType.availableInventory, ticketType.perOrderLimit)}
                                  value={quantities[ticketType.id] ?? 0}
                                  onChange={(eventInput) =>
                                    setQuantities((previous) => ({
                                      ...previous,
                                      [ticketType.id]: Number(eventInput.target.value)
                                    }))
                                  }
                                />
                                <p className="field-hint">Choose up to {ticketType.perOrderLimit} per checkout.</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <form className="panel stack" onSubmit={handleCheckout}>
                  <div className="section-head">
                    <div>
                      <div className="eyebrow">Checkout</div>
                      <h2>Secure your booking</h2>
                    </div>
                  </div>
                  <div className="field">
                    <label htmlFor="buyer-name">Buyer name</label>
                    <input
                      id="buyer-name"
                      placeholder="Alex Guest"
                      value={buyerName}
                      onChange={(eventInput) => setBuyerName(eventInput.target.value)}
                    />
                    <p className="field-hint">Name used on the order and any attendee defaults.</p>
                  </div>
                  <div className="field">
                    <label htmlFor="buyer-email">Buyer email</label>
                    <input
                      id="buyer-email"
                      type="email"
                      placeholder="alex@example.com"
                      value={buyerEmail}
                      onChange={(eventInput) => setBuyerEmail(eventInput.target.value)}
                    />
                    <p className="field-hint">Your receipt and booking confirmation will be sent here.</p>
                  </div>
                  <div className="field">
                    <label htmlFor="buyer-note">Optional note</label>
                    <textarea
                      id="buyer-note"
                      placeholder="Add any message for the organiser, such as accessibility needs or arrival notes."
                      value={note}
                      maxLength={500}
                      onChange={(eventInput) => setNote(eventInput.target.value)}
                    />
                    <p className="field-hint">Optional. This note is shown to the organiser in the order dashboard.</p>
                  </div>
                  <div className="notice">You will be taken to secure payment after you confirm your selection.</div>
                  <div className="meta-list">
                    <div>
                      <strong>Order total</strong>
                      <span>{formatMoney(total || 0, event.ticketTypes[0]?.currency ?? "GBP")}</span>
                    </div>
                    <div>
                      <strong>Selected lines</strong>
                      <span>{selectedTickets.length}</span>
                    </div>
                  </div>
                  {message ? <div className="error-banner">{message}</div> : null}
                  <button className="button" type="submit" disabled={submitting || selectedTickets.length === 0}>
                    {submitting ? "Preparing secure checkout…" : "Continue to payment"}
                  </button>
                </form>
              </section>
            </>
          );
        }}
      </LoadGate>
    </PublicShell>
  );
}

function SuccessPage() {
  const sessionId = new URLSearchParams(window.location.search).get("session_id") ?? "";
  const [state, setState] = useState<AsyncState<SessionStatus>>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      try {
        const payload = await api<SessionStatus>(`/api/checkout/session-status?session_id=${encodeURIComponent(sessionId)}`);
        if (!cancelled) {
          setState({ status: "success", data: payload });
          if (payload.status === "pending" && attempts < 10) {
            attempts += 1;
            window.setTimeout(poll, 3000);
          }
        }
      } catch (error) {
        if (!cancelled) {
          setState({ status: "error", message: error instanceof Error ? error.message : "Failed to load session status" });
        }
      }
    };
    if (sessionId) {
      poll();
    } else {
      setState({ status: "error", message: "This confirmation link is missing its booking reference." });
    }
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <PublicShell>
      <section className="section">
        <div className="section-head">
          <div>
            <div className="eyebrow">Booking update</div>
            <h2>We are confirming your order</h2>
          </div>
        </div>
        <LoadGate state={state}>
          {(session) => (
            <div className="panel stack">
              <div className={statusClass(session.status)}>{session.status}</div>
              <div className="meta-list">
                <div>
                  <strong>Order number</strong>
                  <span>{session.publicOrderNo}</span>
                </div>
                <div>
                  <strong>Event</strong>
                  <span>{session.event.title}</span>
                </div>
                    <div>
                      <strong>Status detail</strong>
                      <span>
                        {session.status === "paid"
                          ? "Your payment has been confirmed."
                          : session.status === "pending"
                            ? "Your payment is still being checked. This page will update automatically."
                            : "This booking is no longer active."}
                      </span>
                    </div>
                  </div>
            </div>
          )}
        </LoadGate>
      </section>
    </PublicShell>
  );
}

function CancelPage() {
  const orderNo = new URLSearchParams(window.location.search).get("order_no");
  return (
    <PublicShell>
      <section className="section">
        <div className="panel stack">
          <div className="eyebrow">Booking cancelled</div>
          <h2>Your payment was not completed.</h2>
          <p>
            Booking reference <strong>{orderNo ?? "unknown"}</strong> has not been confirmed. You can return to the event
            page and start again whenever you are ready.
          </p>
          <div className="actions">
            <a className="button" href="/events">
              Back to programme
            </a>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}

function StaticPage({ page }: { page: StaticPageName }) {
  const settingsState = usePublicSiteSettings();
  const settings = settingsState.status === "success" ? settingsState.data.settings : defaultPublicSiteSettings;
  const content = {
    terms: {
      title: "Terms",
      body: "Tickets are subject to availability, event timings, venue rules, and any updates shared by the organiser."
    },
    privacy: {
      title: "Privacy",
      body: "We use your booking details to fulfil orders, send confirmations, and support entry on the day of the event."
    },
    "refund-policy": {
      title: "Refund policy",
      body: "Refund windows, transfer rules, and event-specific conditions are set by the organiser and shown on each event page."
    },
    contact: {
      title: "Contact",
      body: `For booking questions, accessibility requests, or event support, contact ${settings.supportEmail}.`
    }
  }[page];

  return (
    <PublicShell>
      <section className="section">
        <div className="panel stack">
          <div className="eyebrow">Info</div>
          <h2>{content.title}</h2>
          <p>{content.body}</p>
          <div className="footer-links">
            <a href="/terms">Terms</a>
            <a href="/privacy">Privacy</a>
            <a href="/refund-policy">Refund policy</a>
            <a href="/contact">Contact</a>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}

function useAdminMe() {
  return useLoad(() => api<AdminMe>("/admin/api/me"), []);
}

function AdminOverviewPage() {
  const meState = useAdminMe();
  return (
    <LoadGate state={meState}>
      {(me) => (
        <AdminShell me={me} title="Overview">
          <div className="admin-grid">
            <div className="stat">
              <strong>Identity</strong>
              <h3>{me.email}</h3>
              <p className="muted">Cloudflare Access identity mapped into the local `admins` table.</p>
            </div>
            <div className="stat">
              <strong>Route boundary</strong>
              <h3>/admin*</h3>
              <p className="muted">Both pages and admin API stay under the same protected path prefix.</p>
            </div>
            <div className="stat">
              <strong>Role</strong>
              <h3>{me.role}</h3>
              <p className="muted">Write routes also perform same-origin checks before mutation.</p>
            </div>
            <div className="stat">
              <strong>Publishing flow</strong>
              <h3>Draft → Ticket types → Publish</h3>
              <p className="muted">Create the event, add at least one visible ticket type, then press Publish.</p>
            </div>
          </div>
        </AdminShell>
      )}
    </LoadGate>
  );
}

function AdminEventsPage() {
  const meState = useAdminMe();
  const eventsState = useLoad(() => api<{ events: PublicEvent[] }>("/admin/api/events"), []);

  return (
    <LoadGate state={meState}>
      {(me) => (
        <AdminShell me={me} title="Events">
          <div className="actions">
            <a className="button" href="/admin/events/new">
              Create event
            </a>
          </div>
          <LoadGate state={eventsState}>
            {({ events }) => (
              <div className="cards">
                {events.map((event) => (
                  <article className="card" key={event.id}>
                    <div className="card-body stack">
                      <div className={statusClass(event.status)}>{event.status}</div>
                      <h3>{event.title}</h3>
                      <p>{event.shortDescription}</p>
                      <div className="actions">
                        <a className="button" href={`/admin/events/${event.id}`}>
                          Edit
                        </a>
                        <a className="ghost-button" href={`/events/${event.slug}`}>
                          Public page
                        </a>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </LoadGate>
        </AdminShell>
      )}
    </LoadGate>
  );
}

const defaultEventForm = {
  slug: "",
  title: "",
  shortDescription: "",
  longDescription: "",
  venueName: "",
  venueAddress: "",
  startAt: "",
  endAt: "",
  heroImageUrl: "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=1200&q=80",
  status: "draft",
  refundPolicy: "",
  contactEmail: "tickets@example.com"
};

const defaultTicketTypeForm = {
  eventId: "",
  name: "",
  description: "",
  priceMinor: 1250,
  currency: "GBP",
  inventoryTotal: 50,
  perOrderLimit: 4,
  saleStartAt: "",
  saleEndAt: "",
  isVisible: true,
  displayOrder: 1
};

const defaultPromoCodeForm: PromoCodeFormState = {
  code: "",
  discountType: "percent",
  discountValue: "20",
  currency: "GBP",
  maxRedemptions: "",
  expiresAt: "",
  isActive: true
};

function createPromoCodeForm(): PromoCodeFormState {
  return { ...defaultPromoCodeForm };
}

function toPromoCodeFormState(promoCode: AdminPromoCode): PromoCodeFormState {
  return {
    id: promoCode.id,
    code: promoCode.code,
    discountType: promoCode.discountType,
    discountValue: String(promoCode.discountValue),
    currency: promoCode.currency ?? "GBP",
    maxRedemptions: promoCode.maxRedemptions === null ? "" : String(promoCode.maxRedemptions),
    expiresAt: promoCode.expiresAt ? toDateTimeLocalValue(promoCode.expiresAt) : "",
    isActive: promoCode.isActive
  };
}

function toPromoCodePayload(promoCode: PromoCodeFormState) {
  return {
    ...(promoCode.id ? { id: promoCode.id } : {}),
    code: promoCode.code.trim().toUpperCase(),
    discountType: promoCode.discountType,
    discountValue: Number(promoCode.discountValue),
    currency: promoCode.discountType === "amount" ? promoCode.currency : null,
    maxRedemptions: promoCode.maxRedemptions.trim() ? Number(promoCode.maxRedemptions) : null,
    expiresAt: promoCode.expiresAt ? fromDateTimeLocalValue(promoCode.expiresAt) : null,
    isActive: promoCode.isActive
  };
}

function AdminEventEditorPage({ eventId }: { eventId?: string }) {
  const meState = useAdminMe();
  const eventState = useLoad(
    () => (eventId ? api<{ event: AdminEvent }>(`/admin/api/events/${eventId}`) : Promise.resolve({ event: null as AdminEvent | null })),
    [eventId]
  );
  const [eventForm, setEventForm] = useState(defaultEventForm);
  const [ticketTypeForm, setTicketTypeForm] = useState(defaultTicketTypeForm);
  const [promoCodeForms, setPromoCodeForms] = useState<PromoCodeFormState[]>([]);
  const [message, setMessage] = useState("");
  const [eventAction, setEventAction] = useState<"draft" | "publish" | "status" | null>(null);
  const [ticketTypeAction, setTicketTypeAction] = useState<"create" | "update" | "delete" | null>(null);
  const [editingTicketTypeId, setEditingTicketTypeId] = useState<string | null>(null);
  const heroImageInputRef = useRef<HTMLInputElement | null>(null);

  function resetTicketTypeEditor(event: PublicEvent | null) {
    setEditingTicketTypeId(null);
    setTicketTypeForm({
      ...defaultTicketTypeForm,
      eventId: event?.id ?? "",
      saleStartAt: event ? toDateTimeLocalValue(event.startAt) : "",
      saleEndAt: event ? toDateTimeLocalValue(event.endAt) : ""
    });
  }

  useEffect(() => {
    if (eventState.status === "success" && eventState.data.event) {
      const event = eventState.data.event;
      setEventForm({
        slug: event.slug,
        title: event.title,
        shortDescription: event.shortDescription,
        longDescription: event.longDescription,
        venueName: event.venueName,
        venueAddress: event.venueAddress,
        startAt: toDateTimeLocalValue(event.startAt),
        endAt: toDateTimeLocalValue(event.endAt),
        heroImageUrl: event.heroImageUrl,
        status: event.status,
        refundPolicy: event.refundPolicy,
        contactEmail: event.contactEmail
      });
      setPromoCodeForms(event.promoCodes.map(toPromoCodeFormState));
      if (!editingTicketTypeId) {
        resetTicketTypeEditor(event);
      }
    }
  }, [editingTicketTypeId, eventState]);

  async function persistEvent(nextStatus: typeof eventForm.status, action: "draft" | "publish" | "status") {
    setEventAction(action);
    try {
      const path = eventId ? `/admin/api/events/${eventId}` : "/admin/api/events";
              const response = await api<{ event_id: string }>(path, {
                method: eventId ? "PUT" : "POST",
                body: JSON.stringify({
                  ...eventForm,
                  startAt: fromDateTimeLocalValue(eventForm.startAt),
                  endAt: fromDateTimeLocalValue(eventForm.endAt),
                  status: nextStatus,
                  promoCodes: promoCodeForms
                    .filter((promoCode) => !promoCode.isArchived && promoCode.code.trim())
                    .map(toPromoCodePayload)
                })
              });
      setEventForm((previous) => ({
        ...previous,
        status: nextStatus
      }));
      const statusLabel =
        nextStatus === "draft"
          ? "Saved draft"
          : nextStatus === "published"
            ? "Published event"
            : `Saved ${nextStatus} event`;
      setMessage(`${statusLabel} ${response.event_id}`);
      if (!eventId) {
        window.location.assign(`/admin/events/${response.event_id}`);
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Save failed");
    } finally {
      setEventAction(null);
    }
  }

  async function handleHeroImageSelection(eventInput: React.ChangeEvent<HTMLInputElement>) {
    const file = eventInput.target.files?.[0];
    eventInput.target.value = "";
    if (!file) {
      return;
    }
    if (!/^(image\/(png|jpeg|webp|gif|avif))$/i.test(file.type)) {
      setMessage("Choose an image file such as JPG, PNG, WEBP, or GIF.");
      return;
    }
    if (file.size > maxHeroImageBytes) {
      setMessage("Choose an image under 1.5 MB so the event saves quickly.");
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setEventForm((previous) => ({
        ...previous,
        heroImageUrl: dataUrl
      }));
      setMessage(`Loaded cover image ${file.name}. Save the event to keep it.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Image upload failed");
    }
  }

  async function persistTicketType() {
    if (!eventId) {
      return;
    }
    const action = editingTicketTypeId ? "update" : "create";
    setTicketTypeAction(action);
    try {
      const path = editingTicketTypeId ? `/admin/api/ticket-types/${editingTicketTypeId}` : "/admin/api/ticket-types";
      const method = editingTicketTypeId ? "PUT" : "POST";
      const response = await api<{ ticket_type_id: string }>(path, {
        method,
        body: JSON.stringify({
          ...ticketTypeForm,
          eventId,
          saleStartAt: fromDateTimeLocalValue(ticketTypeForm.saleStartAt),
          saleEndAt: fromDateTimeLocalValue(ticketTypeForm.saleEndAt)
        })
      });
      setMessage(
        editingTicketTypeId
          ? `Updated ticket type ${response.ticket_type_id}. Reloading the editor now.`
          : `Created ticket type ${response.ticket_type_id}. Reloading the editor now.`
      );
      window.location.assign(`/admin/events/${eventId}`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Ticket type save failed");
    } finally {
      setTicketTypeAction(null);
    }
  }

  async function removeTicketType(ticketType: TicketType) {
    if (!eventId) {
      return;
    }
    const confirmed = window.confirm(`Delete "${ticketType.name}"? This only works if no checkout has used this ticket yet.`);
    if (!confirmed) {
      return;
    }
    setTicketTypeAction("delete");
    try {
      await api(`/admin/api/ticket-types/${ticketType.id}`, {
        method: "DELETE"
      });
      setMessage(`Deleted ticket type ${ticketType.name}. Reloading the editor now.`);
      window.location.assign(`/admin/events/${eventId}`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Ticket type delete failed");
    } finally {
      setTicketTypeAction(null);
    }
  }

  return (
    <LoadGate state={meState}>
      {(me) => (
        <AdminShell me={me} title={eventId ? "Edit event" : "Create event"}>
          <div className="split">
            <div className="panel stack">
              <h2>Event</h2>
              <p className="muted">
                Fill the essentials first: public title, venue, contact email, event window, then press Publish when the
                event is ready to appear on `/` and `/events`.
              </p>
              <div className="field-grid">
                <Field
                  label="Slug"
                  hint="Short URL used after `/events/`. Keep it lowercase with hyphens, for example `noir-night`."
                  placeholder="noir-night"
                  value={eventForm.slug}
                  onChange={(value) => setEventForm((prev) => ({ ...prev, slug: value }))}
                />
                <Field
                  label="Public title"
                  hint="The headline buyers will see on cards, event pages, and checkout."
                  placeholder="Noir Night"
                  value={eventForm.title}
                  onChange={(value) => setEventForm((prev) => ({ ...prev, title: value }))}
                />
                <Field
                  label="Venue"
                  hint="Short venue name shown on event cards and the event detail page."
                  placeholder="Velvet Hall"
                  value={eventForm.venueName}
                  onChange={(value) => setEventForm((prev) => ({ ...prev, venueName: value }))}
                />
                <Field
                  label="Contact email"
                  hint="Buyer-facing support mailbox for this event."
                  placeholder="tickets@oasissoton.com"
                  type="email"
                  value={eventForm.contactEmail}
                  onChange={(value) => setEventForm((prev) => ({ ...prev, contactEmail: value }))}
                />
                <Field
                  label="Start time"
                  hint="Pick the public start time shown on the site and used to sort events."
                  type="datetime-local"
                  value={eventForm.startAt}
                  onChange={(value) => setEventForm((prev) => ({ ...prev, startAt: value }))}
                />
                <Field
                  label="End time"
                  hint="Pick when the event window ends. Use the same timezone as the venue schedule."
                  type="datetime-local"
                  value={eventForm.endAt}
                  onChange={(value) => setEventForm((prev) => ({ ...prev, endAt: value }))}
                />
                <Field
                  label="Status"
                  hint="Use Draft while editing. Publish when the event should go live."
                  value={eventForm.status}
                  options={eventStatusOptions}
                  onChange={(value) => setEventForm((prev) => ({ ...prev, status: value }))}
                />
              </div>
              <div className="field">
                <label htmlFor="hero-image-upload">Cover image</label>
                <div className="image-upload-card">
                  <div className="image-upload-preview" style={{ backgroundImage: `url(${eventForm.heroImageUrl})` }} />
                  <div className="stack">
                    <p className="field-hint">Pick a poster or event image from your computer. This becomes the card and hero artwork.</p>
                    <div className="actions">
                      <button
                        className="ghost-button"
                        type="button"
                        onClick={() => heroImageInputRef.current?.click()}
                      >
                        Upload image
                      </button>
                      <button
                        className="ghost-button"
                        type="button"
                        onClick={() =>
                          setEventForm((previous) => ({
                            ...previous,
                            heroImageUrl: defaultEventForm.heroImageUrl
                          }))
                        }
                      >
                        Use sample cover
                      </button>
                    </div>
                    <input
                      ref={heroImageInputRef}
                      id="hero-image-upload"
                      className="sr-only"
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
                      onChange={handleHeroImageSelection}
                    />
                    <Field
                      label="Or paste an image URL"
                      hint="Only use this if the artwork already lives online. Otherwise the upload button above is easier."
                      type="url"
                      placeholder="https://images.example.com/noir-night.jpg"
                      value={eventForm.heroImageUrl.startsWith("data:image/") ? "" : eventForm.heroImageUrl}
                      onChange={(value) =>
                        setEventForm((previous) => ({
                          ...previous,
                          heroImageUrl: value.trim() || defaultEventForm.heroImageUrl
                        }))
                      }
                    />
                  </div>
                </div>
              </div>
              <Field
                label="Card summary"
                hint="One short paragraph for the event card and event list."
                textarea
                placeholder="A late-night showcase with a velvet-room atmosphere and limited floor capacity."
                value={eventForm.shortDescription}
                onChange={(value) => setEventForm((prev) => ({ ...prev, shortDescription: value }))}
              />
              <Field
                label="Full event description"
                hint="Long-form copy used on the event page before checkout."
                textarea
                placeholder="Describe the programme, audience, dress code, or any other buyer-facing detail."
                value={eventForm.longDescription}
                onChange={(value) => setEventForm((prev) => ({ ...prev, longDescription: value }))}
              />
              <Field
                label="Venue address"
                hint="Full address or directions buyers may need on the day."
                textarea
                placeholder="11 Palace Street, London SW1E 5JA"
                value={eventForm.venueAddress}
                onChange={(value) => setEventForm((prev) => ({ ...prev, venueAddress: value }))}
              />
              <Field
                label="Refund policy"
                hint="One short policy statement shown publicly on the event page."
                textarea
                placeholder="Refunds close 48 hours before doors unless the promoter cancels the event."
                value={eventForm.refundPolicy}
                onChange={(value) => setEventForm((prev) => ({ ...prev, refundPolicy: value }))}
              />
              <p className="muted">Save draft keeps the event off public pages. Publish sends it live on `/` and `/events`.</p>
              {message ? <div className="notice">{message}</div> : null}
              <div className="actions">
                <button
                  className="button"
                  type="button"
                  disabled={eventAction !== null}
                  onClick={() => persistEvent("draft", "draft")}
                >
                  {eventAction === "draft" ? "Saving draft..." : "Save draft"}
                </button>
                <button
                  className="ghost-button"
                  type="button"
                  disabled={eventAction !== null}
                  onClick={() => persistEvent("published", "publish")}
                >
                  {eventAction === "publish" ? "Publishing..." : "Publish"}
                </button>
                {(eventForm.status === "closed" || eventForm.status === "archived") ? (
                  <button
                    className="ghost-button"
                    type="button"
                    disabled={eventAction !== null}
                    onClick={() => persistEvent(eventForm.status, "status")}
                  >
                    {eventAction === "status" ? "Saving..." : `Apply ${eventForm.status}`}
                  </button>
                ) : null}
	              </div>
	            </div>

	            <div className="panel stack">
	              <h2>Promo codes</h2>
	              <p className="muted">
	                Buyers enter these codes on Stripe Checkout. Each code stays scoped to this event only.
	              </p>
	              {promoCodeForms.filter((promoCode) => !promoCode.isArchived).length === 0 ? (
	                <div className="notice">No promo codes yet. Save without discounts or add one below.</div>
	              ) : (
	                promoCodeForms
	                  .filter((promoCode) => !promoCode.isArchived)
	                  .map((promoCode, index) => (
	                    <div className="card" key={promoCode.id ?? `promo-${index}`}>
	                      <div className="card-body stack">
	                        <h3>Promo #{index + 1}</h3>
	                        <div className="compact-grid">
	                          <Field
	                            label="Code"
	                            hint="The code buyers type on Stripe Checkout."
	                            placeholder="EARLYBIRD20"
	                            value={promoCode.code}
	                            onChange={(value) =>
	                              setPromoCodeForms((previous) =>
	                                previous.map((candidate, candidateIndex) =>
	                                  candidateIndex === index ? { ...candidate, code: value.toUpperCase() } : candidate
	                                )
	                              )
	                            }
	                          />
	                          <Field
	                            label="Discount type"
	                            hint="Choose whether Stripe applies a percentage or fixed amount."
	                            value={promoCode.discountType}
	                            options={promoDiscountTypeOptions}
	                            onChange={(value) =>
	                              setPromoCodeForms((previous) =>
	                                previous.map((candidate, candidateIndex) =>
	                                  candidateIndex === index
	                                    ? { ...candidate, discountType: value as "percent" | "amount" }
	                                    : candidate
	                                )
	                              )
	                            }
	                          />
	                          <Field
	                            label={promoCode.discountType === "percent" ? "Percent off" : "Amount off in minor units"}
	                            hint={
	                              promoCode.discountType === "percent"
	                                ? "Example: `20` means 20% off."
	                                : "Example: `500` means £5.00 off when the currency is GBP."
	                            }
	                            type="number"
	                            min={1}
	                            max={promoCode.discountType === "percent" ? 100 : undefined}
	                            step={promoCode.discountType === "percent" ? 0.01 : 1}
	                            value={promoCode.discountValue}
	                            onChange={(value) =>
	                              setPromoCodeForms((previous) =>
	                                previous.map((candidate, candidateIndex) =>
	                                  candidateIndex === index ? { ...candidate, discountValue: value } : candidate
	                                )
	                              )
	                            }
	                          />
	                          <Field
	                            label="Currency"
	                            hint="Only used for fixed-amount discounts."
	                            value={promoCode.currency}
	                            options={currencyOptions}
	                            onChange={(value) =>
	                              setPromoCodeForms((previous) =>
	                                previous.map((candidate, candidateIndex) =>
	                                  candidateIndex === index ? { ...candidate, currency: value } : candidate
	                                )
	                              )
	                            }
	                          />
	                          <Field
	                            label="Max redemptions"
	                            hint="Leave blank if the code should not have a usage cap."
	                            type="number"
	                            min={1}
	                            step={1}
	                            value={promoCode.maxRedemptions}
	                            onChange={(value) =>
	                              setPromoCodeForms((previous) =>
	                                previous.map((candidate, candidateIndex) =>
	                                  candidateIndex === index ? { ...candidate, maxRedemptions: value } : candidate
	                                )
	                              )
	                            }
	                          />
	                          <Field
	                            label="Expiry"
	                            hint="Optional. Leave blank to keep the code available until you deactivate it."
	                            type="datetime-local"
	                            value={promoCode.expiresAt}
	                            onChange={(value) =>
	                              setPromoCodeForms((previous) =>
	                                previous.map((candidate, candidateIndex) =>
	                                  candidateIndex === index ? { ...candidate, expiresAt: value } : candidate
	                                )
	                              )
	                            }
	                          />
	                          <Field
	                            label="Active"
	                            hint="Inactive codes remain configured but Stripe will not accept them."
	                            value={String(promoCode.isActive)}
	                            options={yesNoOptions}
	                            onChange={(value) =>
	                              setPromoCodeForms((previous) =>
	                                previous.map((candidate, candidateIndex) =>
	                                  candidateIndex === index ? { ...candidate, isActive: value === "true" } : candidate
	                                )
	                              )
	                            }
	                          />
	                        </div>
	                        <div className="actions">
	                          <button
	                            className="ghost-button"
	                            type="button"
	                            onClick={() =>
	                              setPromoCodeForms((previous) =>
	                                previous.map((candidate, candidateIndex) =>
	                                  candidateIndex === index ? { ...candidate, isArchived: true } : candidate
	                                )
	                              )
	                            }
	                          >
	                            {promoCode.id ? "Archive promo code" : "Remove promo code"}
	                          </button>
	                        </div>
	                      </div>
	                    </div>
	                  ))
	              )}
	              <div className="actions">
	                <button
	                  className="ghost-button"
	                  type="button"
	                  onClick={() => setPromoCodeForms((previous) => [...previous, createPromoCodeForm()])}
	                >
	                  Add promo code
	                </button>
	              </div>
	            </div>

	            <div className="panel stack">
	              <h2>Ticket types</h2>
              <p className="muted">
                Add at least one visible ticket type before you test checkout. Buyers cannot purchase a published event with
                no ticket rows.
              </p>
              {eventState.status === "success" && eventState.data.event ? (
                <div className="stack">
                  {eventState.data.event.ticketTypes.map((ticketType) => (
                    <div className="card" key={ticketType.id}>
                      <div className="card-body stack">
                        <h3>{ticketType.name}</h3>
                        <p>{formatMoney(ticketType.priceMinor, ticketType.currency)}</p>
                        <span className="muted">
                          Available: {ticketType.availableInventory} · Per order: {ticketType.perOrderLimit} ·{" "}
                          {ticketType.isVisible ? "Visible" : "Hidden"}
                        </span>
                        <div className="actions">
                          <button
                            className="ghost-button"
                            type="button"
                            onClick={() => {
                              setEditingTicketTypeId(ticketType.id);
                              setTicketTypeForm(toTicketTypeFormState(ticketType));
                              setMessage(`Editing ticket type ${ticketType.name}.`);
                            }}
                          >
                            Edit ticket
                          </button>
                          <button
                            className="danger-button"
                            type="button"
                            disabled={ticketTypeAction === "delete"}
                            onClick={() => void removeTicketType(ticketType)}
                          >
                            {ticketTypeAction === "delete" ? "Deleting..." : "Delete"}
                          </button>
                        </div>
                        <p className="field-hint">Delete only works before any checkout has used this ticket type.</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="muted">Save the event first, then create ticket types.</p>
              )}
              <h3>{editingTicketTypeId ? "Edit ticket type" : "Add ticket type"}</h3>
              <div className="compact-grid">
                <Field
                  label="Ticket name"
                  hint="Label shown to buyers, for example `Standard`, `VIP`, or `Balcony`."
                  placeholder="Standard"
                  value={ticketTypeForm.name}
                  onChange={(value) => setTicketTypeForm((prev) => ({ ...prev, name: value }))}
                />
                <Field
                  label="Price in minor units"
                  hint="Use the smallest currency unit. Example: `1250` means £12.50."
                  type="number"
                  min={0}
                  step={1}
                  value={String(ticketTypeForm.priceMinor)}
                  onChange={(value) => setTicketTypeForm((prev) => ({ ...prev, priceMinor: Number(value) }))}
                />
                <Field
                  label="Currency"
                  hint="Choose the currency buyers will see at checkout."
                  value={ticketTypeForm.currency}
                  options={currencyOptions}
                  onChange={(value) => setTicketTypeForm((prev) => ({ ...prev, currency: value }))}
                />
                <Field
                  label="Inventory"
                  hint="Maximum number of tickets available for this tier."
                  type="number"
                  min={1}
                  step={1}
                  value={String(ticketTypeForm.inventoryTotal)}
                  onChange={(value) => setTicketTypeForm((prev) => ({ ...prev, inventoryTotal: Number(value) }))}
                />
                <Field
                  label="Per-order limit"
                  hint="Maximum tickets one buyer can take in a single checkout."
                  type="number"
                  min={1}
                  step={1}
                  value={String(ticketTypeForm.perOrderLimit)}
                  onChange={(value) => setTicketTypeForm((prev) => ({ ...prev, perOrderLimit: Number(value) }))}
                />
                <Field
                  label="Display order"
                  hint="Lower numbers appear first in the ticket list."
                  type="number"
                  min={1}
                  step={1}
                  value={String(ticketTypeForm.displayOrder)}
                  onChange={(value) => setTicketTypeForm((prev) => ({ ...prev, displayOrder: Number(value) }))}
                />
                <Field
                  label="Visible to buyers"
                  hint="Choose No to keep the ticket hidden while you finish setup."
                  value={String(ticketTypeForm.isVisible)}
                  options={yesNoOptions}
                  onChange={(value) => setTicketTypeForm((prev) => ({ ...prev, isVisible: value === "true" }))}
                />
              </div>
              <Field
                label="Description"
                hint="Short buyer-facing explanation for what this ticket includes."
                textarea
                placeholder="General entry with standing room access."
                value={ticketTypeForm.description}
                onChange={(value) => setTicketTypeForm((prev) => ({ ...prev, description: value }))}
              />
              <div className="compact-grid">
                <Field
                  label="Sale start"
                  hint="Pick when checkout for this ticket should open."
                  type="datetime-local"
                  value={ticketTypeForm.saleStartAt}
                  onChange={(value) => setTicketTypeForm((prev) => ({ ...prev, saleStartAt: value }))}
                />
                <Field
                  label="Sale end"
                  hint="Pick when checkout for this ticket should stop."
                  type="datetime-local"
                  value={ticketTypeForm.saleEndAt}
                  onChange={(value) => setTicketTypeForm((prev) => ({ ...prev, saleEndAt: value }))}
                />
              </div>
              <div className="actions">
                <button className="ghost-button" type="button" disabled={!eventId || ticketTypeAction !== null} onClick={persistTicketType}>
                  {ticketTypeAction === "create"
                    ? "Adding ticket..."
                    : ticketTypeAction === "update"
                      ? "Saving ticket..."
                      : editingTicketTypeId
                        ? "Save ticket changes"
                        : "Add ticket type"}
                </button>
                {editingTicketTypeId ? (
                  <button
                    className="ghost-button"
                    type="button"
                    disabled={ticketTypeAction !== null}
                    onClick={() => {
                      const currentEvent = eventState.status === "success" ? eventState.data.event : null;
                      resetTicketTypeEditor(currentEvent);
                      setMessage("Stopped editing the selected ticket type.");
                    }}
                  >
                    Cancel editing
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </AdminShell>
      )}
    </LoadGate>
  );
}

function AdminOrdersPage() {
  const meState = useAdminMe();
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const ordersState = useLoad(
    () =>
      api<{ orders: OrderSummary[] }>(
        `/admin/api/orders?q=${encodeURIComponent(query)}&status=${encodeURIComponent(status)}`
      ),
    [query, status]
  );

  return (
    <LoadGate state={meState}>
      {(me) => (
        <AdminShell me={me} title="Orders">
          <div className="panel stack">
            <div className="compact-grid">
              <Field
                label="Search by order no / buyer email"
                hint="Paste a buyer email or an order number like `ST-20260416-68KSEU`."
                placeholder="buyer@example.com or ST-..."
                value={query}
                onChange={setQuery}
              />
              <Field
                label="Order status"
                hint="Use this to narrow the table to one order state."
                value={status}
                options={orderStatusOptions}
                onChange={setStatus}
              />
            </div>
            <LoadGate state={ordersState}>
              {({ orders }) => (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Order</th>
                        <th>Buyer</th>
                        <th>Event</th>
                        <th>Status</th>
                        <th>Total</th>
                        <th>Paid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order) => (
                        <tr key={order.id}>
                          <td>
                            <a href={`/admin/orders/${order.id}`}>{order.publicOrderNo}</a>
                          </td>
                          <td>
                            <div className="stack">
                              <span>{order.buyerEmail}</span>
                              {order.note ? (
                                <span className="muted">
                                  Note: {order.note.length > 80 ? `${order.note.slice(0, 80)}…` : order.note}
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td>{order.event.title}</td>
                          <td>
                            <span className={statusClass(order.status)}>{order.status}</span>
                          </td>
                          <td>{formatMoney(order.totalPriceMinor, order.currency)}</td>
                          <td>{order.paidAt ? formatDateTime(order.paidAt) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </LoadGate>
          </div>
        </AdminShell>
      )}
    </LoadGate>
  );
}

function AdminOrderDetailPage({ orderId }: { orderId: string }) {
  const meState = useAdminMe();
  const orderState = useLoad(() => api<{ order: OrderDetail }>(`/admin/api/orders/${orderId}`), [orderId]);
  const [noteDraft, setNoteDraft] = useState("");
  const [noteMessage, setNoteMessage] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    if (orderState.status === "success") {
      setNoteDraft(orderState.data.order.note ?? "");
    }
  }, [orderState]);

  return (
    <LoadGate state={meState}>
      {(me) => (
        <AdminShell me={me} title="Order detail">
          <LoadGate state={orderState}>
            {({ order }) => (
              <div className="split">
                <div className="panel stack">
                  <div className={statusClass(order.status)}>{order.status}</div>
                  <h2>{order.publicOrderNo}</h2>
                  <div className="meta-list">
                    <div>
                      <strong>Buyer</strong>
                      <span>
                        {order.buyerName} · {order.buyerEmail}
                      </span>
                    </div>
                    <div>
                      <strong>Event</strong>
                      <span>{order.event.title}</span>
                    </div>
                    <div>
                      <strong>Stripe checkout session</strong>
                      <span>{order.stripeCheckoutSessionId ?? "—"}</span>
                    </div>
	                    <div>
	                      <strong>Payment intent</strong>
	                      <span>{order.stripePaymentIntentId ?? "—"}</span>
	                    </div>
	                    <div>
	                      <strong>Promotion code</strong>
	                      <span>{order.appliedPromotionCode ?? "—"}</span>
	                    </div>
	                    <div>
	                      <strong>Discount</strong>
	                      <span>
	                        {order.discountPriceMinor !== null ? formatMoney(order.discountPriceMinor * -1, order.currency) : "—"}
	                      </span>
	                    </div>
	                    <div>
	                      <strong>Amount charged</strong>
	                      <span>{formatMoney(order.paidPriceMinor ?? order.totalPriceMinor, order.currency)}</span>
	                    </div>
	                  </div>
                  <Field
                    label="Order note"
                    hint="Guest-facing note from checkout. You can also adjust it here if the organiser needs to record an update."
                    textarea
                    placeholder="Optional note from the guest or organiser."
                    value={noteDraft}
                    onChange={setNoteDraft}
                  />
                  <div className="actions">
                    <button
                      className="button"
                      type="button"
                      disabled={savingNote}
                      onClick={async () => {
                        try {
                          setSavingNote(true);
                          setNoteMessage("");
                          await api(`/admin/api/orders/${order.id}`, {
                            method: "PUT",
                            body: JSON.stringify({ note: noteDraft })
                          });
                          setNoteMessage("Order note saved.");
                        } catch (error) {
                          setNoteMessage(error instanceof Error ? error.message : "Note update failed");
                        } finally {
                          setSavingNote(false);
                        }
                      }}
                    >
                      {savingNote ? "Saving note…" : "Save note"}
                    </button>
                  </div>
                  {noteMessage ? <div className="notice">{noteMessage}</div> : null}
                </div>
                <div className="panel stack">
                  <h2>Items & attendees</h2>
                  {order.items.map((item) => (
                    <div className="card" key={item.id}>
                      <div className="card-body stack">
                        <strong>
                          {item.ticketTypeName} × {item.quantity}
                        </strong>
                        <span>{formatMoney(item.lineTotalMinor, order.currency)}</span>
                        {item.attendees.map((attendee) => (
                          <div key={attendee.ticketCode} className="meta-list">
                            <div>
                              <strong>{attendee.name}</strong>
                              <span>
                                {attendee.email} · {attendee.ticketCode}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </LoadGate>
        </AdminShell>
      )}
    </LoadGate>
  );
}

function AdminCheckinPage({ eventId }: { eventId: string }) {
  const meState = useAdminMe();
  const [ticketCode, setTicketCode] = useState("");
  const [message, setMessage] = useState("");

  return (
    <LoadGate state={meState}>
      {(me) => (
        <AdminShell me={me} title="Check-in">
          <div className="panel stack">
            <p className="muted">Event scope: {eventId}</p>
            <Field
              label="Ticket code"
              hint="Scan or paste the ticket code printed in the attendee record."
              placeholder="TKT-ABC123"
              value={ticketCode}
              onChange={setTicketCode}
            />
            <div className="actions">
              <button
                className="button"
                type="button"
                onClick={async () => {
                  try {
                    const result = await api<{
                      attendeeName: string;
                      attendeeEmail: string;
                      ticketCode: string;
                      eventId: string;
                      eventTitle: string;
                      checkedInAt: string;
                    }>("/admin/api/checkin", {
                      method: "POST",
                      body: JSON.stringify({ ticket_code: ticketCode })
                    });
                    setMessage(`Checked in ${result.attendeeName} for ${result.eventTitle} at ${formatDateTime(result.checkedInAt)}.`);
                  } catch (error) {
                    setMessage(error instanceof Error ? error.message : "Check-in failed");
                  }
                }}
              >
                Check in
              </button>
              <button
                className="danger-button"
                type="button"
                disabled={me.role === "checkin_only"}
                onClick={async () => {
                  try {
                    await api("/admin/api/checkin/revert", {
                      method: "POST",
                      body: JSON.stringify({ ticket_code: ticketCode })
                    });
                    setMessage(`Reverted check-in for ${ticketCode}.`);
                  } catch (error) {
                    setMessage(error instanceof Error ? error.message : "Revert failed");
                  }
                }}
              >
                Revert
              </button>
            </div>
            {message ? <div className="notice">{message}</div> : null}
          </div>
        </AdminShell>
      )}
    </LoadGate>
  );
}

function AdminSettingsPage() {
  const meState = useAdminMe();
  const settingsState = useLoad(() => api<{ settings: Record<string, unknown> }>("/admin/api/settings"), []);
  const [settingsSnapshot, setSettingsSnapshot] = useState<Record<string, unknown>>({});
  const [settingsForm, setSettingsForm] = useState<SiteSettingsFormState>(defaultSettingsForm);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (settingsState.status === "success") {
      const settings = settingsState.data.settings;
      setSettingsSnapshot(settings);
      const branding = isRecord(settings.branding) ? settings.branding : {};
      const email = isRecord(settings.email) ? settings.email : {};
      setSettingsForm({
        siteName: typeof branding.siteName === "string" ? branding.siteName : defaultSettingsForm.siteName,
        supportEmail: typeof branding.supportEmail === "string" ? branding.supportEmail : defaultSettingsForm.supportEmail,
        heroNotice: typeof branding.heroNotice === "string" ? branding.heroNotice : defaultSettingsForm.heroNotice,
        emailEnabled: String(Boolean(email.enabled)),
        fromEmail: typeof email.fromEmail === "string" ? email.fromEmail : ""
      });
    }
  }, [settingsState]);

  return (
    <LoadGate state={meState}>
      {(me) => (
        <AdminShell me={me} title="Settings">
          {me.role !== "owner" ? (
            <div className="error-banner">Only owners can edit site settings in this stage.</div>
          ) : (
            <div className="panel stack">
              <p className="muted">
                These settings cover the buyer-facing basics. Fill the site name, support inbox, email sending state, and
                default sender address here instead of editing raw JSON.
              </p>
              <div className="field-grid">
                <Field
                  label="Site name"
                  hint="Displayed in the public header and used as the name guests recognise."
                  placeholder="Noir Nights Ticket Office"
                  value={settingsForm.siteName}
                  onChange={(value) => setSettingsForm((prev) => ({ ...prev, siteName: value }))}
                />
                <Field
                  label="Support email"
                  hint="Public contact address shown to guests who need help with bookings."
                  type="email"
                  placeholder="tickets@oasissoton.com"
                  value={settingsForm.supportEmail}
                  onChange={(value) => setSettingsForm((prev) => ({ ...prev, supportEmail: value }))}
                />
                <Field
                  label="Email sending"
                  hint="Keep this off until Resend is verified. Actual sending still depends on the Cloudflare production variables."
                  value={settingsForm.emailEnabled}
                  options={emailEnabledOptions}
                  onChange={(value) => setSettingsForm((prev) => ({ ...prev, emailEnabled: value }))}
                />
                <Field
                  label="From email"
                  hint="Store the same verified sender address used in Cloudflare, for example `tickets@mg.oasissoton.com`."
                  placeholder="tickets@mg.oasissoton.com"
                  value={settingsForm.fromEmail}
                  onChange={(value) => setSettingsForm((prev) => ({ ...prev, fromEmail: value }))}
                />
              </div>
              <Field
                label="Hero notice"
                hint="Short buyer-facing promise shown as the default ticket office notice."
                textarea
                placeholder="Limited release tickets for intimate late-night events."
                value={settingsForm.heroNotice}
                onChange={(value) => setSettingsForm((prev) => ({ ...prev, heroNotice: value }))}
              />
              <div className="actions">
                <button
                  className="button"
                  type="button"
                  onClick={async () => {
                    try {
                      const branding = isRecord(settingsSnapshot.branding) ? settingsSnapshot.branding : {};
                      const email = isRecord(settingsSnapshot.email) ? settingsSnapshot.email : {};
                      await api("/admin/api/settings", {
                        method: "PUT",
                        body: JSON.stringify({
                          ...settingsSnapshot,
                          branding: {
                            ...branding,
                            siteName: settingsForm.siteName,
                            supportEmail: settingsForm.supportEmail,
                            heroNotice: settingsForm.heroNotice
                          },
                          email: {
                            ...email,
                            enabled: settingsForm.emailEnabled === "true",
                            fromEmail: settingsForm.fromEmail
                          }
                        })
                      });
                      setMessage("Settings saved.");
                    } catch (error) {
                      setMessage(error instanceof Error ? error.message : "Save failed");
                    }
                  }}
                >
                  Save settings
                </button>
              </div>
              {message ? <div className="notice">{message}</div> : null}
            </div>
          )}
        </AdminShell>
      )}
    </LoadGate>
  );
}

function NotFoundPage() {
  return (
    <PublicShell>
      <section className="section">
        <div className="panel stack">
          <div className="eyebrow">404</div>
          <h2>Route not found</h2>
          <p>This Pages SPA route does not exist in the current implementation.</p>
          <div className="actions">
            <a className="button" href="/">
              Return home
            </a>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}

function Field({
  label,
  value,
  onChange,
  textarea,
  type = "text",
  hint,
  placeholder,
  options,
  min,
  max,
  step
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  textarea?: boolean;
  type?: string;
  hint?: string;
  placeholder?: string;
  options?: FieldOption[];
  min?: number;
  max?: number;
  step?: number;
}) {
  const fieldId = useId();

  return (
    <div className="field">
      <label htmlFor={fieldId}>{label}</label>
      {textarea ? (
        <textarea
          id={fieldId}
          placeholder={placeholder}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : options ? (
        <select id={fieldId} value={value} onChange={(event) => onChange(event.target.value)}>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={fieldId}
          type={type}
          min={min}
          max={max}
          step={step}
          placeholder={placeholder}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
      {hint ? <p className="field-hint">{hint}</p> : null}
    </div>
  );
}

export default function App() {
  const route = useRoute();

  switch (route.name) {
    case "home":
      return <HomePage />;
    case "events":
      return <EventsPage />;
    case "event":
      return <EventDetailPage slug={route.slug} />;
    case "checkout-success":
      return <SuccessPage />;
    case "checkout-cancel":
      return <CancelPage />;
    case "static":
      return <StaticPage page={route.page} />;
    case "admin-home":
      return <AdminOverviewPage />;
    case "admin-events":
      return <AdminEventsPage />;
    case "admin-event-new":
      return <AdminEventEditorPage />;
    case "admin-event":
      return <AdminEventEditorPage eventId={route.id} />;
    case "admin-orders":
      return <AdminOrdersPage />;
    case "admin-order":
      return <AdminOrderDetailPage orderId={route.id} />;
    case "admin-checkin":
      return <AdminCheckinPage eventId={route.eventId} />;
    case "admin-settings":
      return <AdminSettingsPage />;
    default:
      return <NotFoundPage />;
  }
}
